import { useState, useMemo } from "react";
import MilestoneModal from "components/modals/MilestoneModal";
import { Btn, Card } from "components/ui/index";
import { T } from "theme/index";
import { fmtIDR, fmtShort } from "utils/format";
import { splitMilestones } from "engine/milestones";

function MilestoneCard({ m, onEdit, onDelete }) {
  const p = m.progress;
  const barColor = p.isComplete ? T.green : p.isOverdue ? T.red : T.blue;
  const groupLabel = m.groupNames.length === 0 ? `Semua grup (${p.itemCount})` : m.groupNames.join(", ");

  return (
    <Card style={{ padding:14, marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
        <div style={{ width:36, height:36, borderRadius:9, background:barColor+"1E", display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0 }}>{m.icon}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ color:T.text, fontSize:13, fontWeight:700 }}>{m.name}</div>
          <div style={{ color:T.muted, fontSize:11, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{groupLabel}</div>
        </div>
        <div style={{ color:barColor, fontSize:14, fontWeight:800, fontFamily:"monospace", flexShrink:0 }}>{Math.round(p.pct*100)}%</div>
      </div>

      <div style={{ height:6, background:T.border, borderRadius:3, overflow:"hidden", marginBottom:8 }}>
        <div style={{ height:"100%", width:"100%", borderRadius:3, transformOrigin:"left", transform:`scaleX(${p.pct})`, background:barColor, transition:"transform 0.3s" }} />
      </div>

      <div style={{ display:"flex", justifyContent:"space-between", color:T.muted, fontSize:11, fontFamily:"monospace", marginBottom:m.targetDate?6:0 }}>
        <span>{fmtIDR(p.current)}</span>
        <span>dari {fmtIDR(p.target)}</span>
      </div>

      {m.targetDate && !p.isComplete && (
        <div style={{ fontSize:11, color:p.isOverdue?T.red:T.muted }}>
          {p.isOverdue
            ? `Target terlewat ${Math.abs(p.daysLeft)} hari lalu`
            : `${p.daysLeft} hari lagi · perlu ~${fmtShort(p.requiredMonthly)}/bulan buat kekejar`}
        </div>
      )}
      {p.isComplete && (
        <div style={{ fontSize:11, color:T.green, fontWeight:700 }}>✅ Tercapai!</div>
      )}

      <div style={{ display:"flex", gap:6, marginTop:10 }}>
        <button onClick={() => onEdit(m)} aria-label={`Edit milestone ${m.name}`} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:6, color:T.muted, cursor:"pointer", padding:"6px 10px", fontSize:11, minHeight:32 }}>Edit</button>
        <button onClick={() => { if(window.confirm(`Hapus milestone "${m.name}"?`)) onDelete(m.id); }} aria-label={`Hapus milestone ${m.name}`} style={{ background:T.red+"15", border:`1px solid ${T.red}28`, borderRadius:6, color:T.red, cursor:"pointer", padding:"6px 10px", fontSize:11, minHeight:32 }}>Hapus</button>
      </div>
    </Card>
  );
}

export default function Milestone({ investments, milestones, onAdd, onEdit, onDelete }) {
  const [modal, setModal] = useState(null);

  const existingGroups = useMemo(() =>
    [...new Set(investments.map(i => i.group).filter(Boolean))].sort(),
  [investments]);

  const { active, completed } = useMemo(() => splitMilestones(milestones, investments), [milestones, investments]);

  return (
    <div>
      {modal && (
        <MilestoneModal
          milestone={modal === "add" ? null : modal}
          existingGroups={existingGroups}
          onSave={modal === "add" ? onAdd : onEdit}
          onClose={() => setModal(null)}
        />
      )}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ color:T.text, fontWeight:700, fontSize:14 }}>Milestone</div>
        <Btn onClick={() => setModal("add")} style={{ fontSize:12, padding:"8px 12px" }}>＋ Milestone</Btn>
      </div>

      {milestones.length === 0 && (
        <Card style={{ padding:48, textAlign:"center" }}>
          <div style={{ fontSize:40, marginBottom:10 }}>🚩</div>
          <div style={{ color:T.text, fontWeight:700, fontSize:14, marginBottom:6 }}>Belum ada milestone</div>
          <div style={{ color:T.muted, fontSize:12, marginBottom:20 }}>Bikin target dari grup investasi yang<br/>udah kamu punya — progress-nya otomatis kehitung.</div>
          <Btn onClick={() => setModal("add")} style={{ margin:"0 auto" }}>＋ Milestone Pertama</Btn>
        </Card>
      )}

      {active.length > 0 && (
        <div style={{ marginBottom:active.length && completed.length ? 20 : 0 }}>
          {active.map(m => <MilestoneCard key={m.id} m={m} onEdit={setModal} onDelete={onDelete} />)}
        </div>
      )}

      {completed.length > 0 && (
        <div>
          <div style={{ color:T.muted, fontSize:11, fontWeight:700, letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:10 }}>Selesai</div>
          {completed.map(m => <MilestoneCard key={m.id} m={m} onEdit={setModal} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
}
